import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { MessageSquare, ChevronRight, CheckCircle } from 'lucide-react';
import { Tooltip, Badge } from '@dopomogai/ui';
import type { ConversationSummary } from '@dopomogai/types';

const formatDisplayDate = (isoDate: string | null): string => {
  if (!isoDate) return 'N/A';
  const date = new Date(isoDate);
  const now = new Date();
  const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;

  const diffDays = Math.floor(diffSeconds / 86400);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
};

const getConversationPreview = (conversation: ConversationSummary): string => {
  return (
    conversation.first_user_message_preview ||
    `Conversation started ${formatDisplayDate(conversation.created_at)}`
  );
};

interface ConversationListItemProps {
  conversation: ConversationSummary;
  detailPageUrl: string;
  className?: string;
  secondaryText?: string;
  hasSummary?: boolean;
}

export const ConversationListItem: React.FC<ConversationListItemProps> = ({
  conversation,
  detailPageUrl,
  className,
  secondaryText,
  hasSummary = false,
}) => {
  const previewText = getConversationPreview(conversation);
  const lastUpdatedText = formatDisplayDate(conversation.last_message_timestamp ?? conversation.updated_at ?? null);
  const createdText = formatDisplayDate(conversation.created_at ?? null);

  return (
    <Link
      to={detailPageUrl}
      className={clsx(
        'block border-2 border-black rounded-xl p-3 bg-white',
        'transition-all duration-150 ease-out transform',
        'hover:-translate-y-px hover:-translate-x-px hover:shadow-[3px_3px_0_0_#000]',
        'active:translate-y-0 active:translate-x-0 active:shadow-none',
        'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 text-slate-500">
          <MessageSquare size={20} />
        </div>

        <div className="flex-grow min-w-0 space-y-1">
          <div className="flex items-center gap-2 min-w-0">
            <Tooltip text={previewText} position="top">
              <p className="font-semibold text-sm text-slate-800 truncate">{previewText}</p>
            </Tooltip>
            {hasSummary && (
              <Badge
                text="Summary"
                className="hidden md:inline-flex text-xs"
                accentColor="emerald"
                icon={<CheckCircle size={12} />}
              />
            )}
          </div>

          {secondaryText && (
            <p className="text-xs text-slate-600 line-clamp-1">{secondaryText}</p>
          )}

          <p className="text-[11px] text-slate-500">
            Created {createdText} • Last updated {lastUpdatedText}
          </p>
        </div>

        <div className="flex-shrink-0 text-slate-400">
          <ChevronRight size={20} />
        </div>
      </div>
    </Link>
  );
};
